export type CompParams = {
  policiesPerMonth: number;
  avgPremium: number;
  firstYearPct: number;
  renewalPct: number;
  recruitsPerYear: number;
};

const RECRUIT_POLICIES_Y1 = 6;
const RECRUIT_AVG_PREMIUM = 1800;
const RECRUIT_OVERRIDE_PCT = 0.1;

export function personalAnnualPremium(p: CompParams): number {
  return p.policiesPerMonth * 12 * p.avgPremium;
}

export function personalYearOne(p: CompParams): number {
  return personalAnnualPremium(p) * (p.firstYearPct / 100);
}

export function overridesYearOne(p: CompParams): number {
  return p.recruitsPerYear * RECRUIT_POLICIES_Y1 * RECRUIT_AVG_PREMIUM * RECRUIT_OVERRIDE_PCT;
}

export function yearOneIncome(p: CompParams): number {
  return Math.round(personalYearOne(p) + overridesYearOne(p));
}

/**
 * Realistic linear-with-renewal-stack model.
 * - Personal: writes the same Y1 commission each year (stable production).
 * - Renewals: each prior year's personal premium base generates renewalPct
 *   renewal commissions, accumulating year over year.
 * - Team overrides: each year of recruiting adds a cohort; each cohort
 *   contributes Y1 override that year, plus a small persistency tail
 *   (renewalPct of override base) in subsequent years.
 */
export function projectedYear(p: CompParams, year: number): number {
  const annualPremium = personalAnnualPremium(p);
  const personalY1 = personalYearOne(p);
  // prior-year renewals stack
  const priorYears = Math.max(0, year - 1);
  const renewals = priorYears * annualPremium * (p.renewalPct / 100);

  const overrideBase = overridesYearOne(p);
  const overrideRenewalPct = p.renewalPct / 100;
  // current-year cohort + tail from prior cohorts
  const overrideTail = priorYears * overrideBase * overrideRenewalPct;

  return Math.round(personalY1 + renewals + overrideBase + overrideTail);
}

export function projectionSeries(p: CompParams): { year: number; income: number }[] {
  return Array.from({ length: 10 }, (_, i) => ({
    year: i + 1,
    income: projectedYear(p, i + 1),
  }));
}

export function averageYears2to5(p: CompParams): number {
  const sum = [2, 3, 4, 5].reduce((acc, y) => acc + projectedYear(p, y), 0);
  return Math.round(sum / 4);
}

const usd = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

export function formatUSD(n: number): string {
  return usd.format(n);
}